<script setup lang="ts">
import type { PropType, Ref } from 'vue'
import { onBeforeUnmount, ref } from 'vue'
import dayjs from 'dayjs'

import type { MessageInterface } from '@voix/types'
import { useVoixToast } from '@voix/composables/useVoixToast'

const props = defineProps({
  message: {
    type: Object as PropType<MessageInterface>,
    required: true,
  },
})

const { remove, defaultTimeout } = useVoixToast()

const timeUntilDestruction = ref(defaultTimeout)
const interval: Ref<any> = ref(null)
const paused = ref(false)

interval.value = setInterval(() => {
  if (!paused.value) {
    timeUntilDestruction.value = dayjs(props.message.expires).diff(
      dayjs(),
      'milliseconds',
    )
  }

  if (paused.value)
    extendExpires()
}, 10)

onBeforeUnmount(() => {
  clearInterval(interval.value)
})

function dismissMessage(id: string) {
  remove(id)
}

function extendExpires() {
  if (props.message.expires) {
    props.message.setExpires(
      dayjs().add(timeUntilDestruction.value, 'milliseconds').toDate(),
    )
  }
}
</script>

<template>
  <div class="relative flex flex-col min-w-[300px] bg-gray-50 shadow rounded">
    <div
      class="max-w-lg"
      @mouseenter="paused = true"
      @mouseleave="paused = false"
    >
      <div class="flex space-x-4 items-center justify-between py-3 px-4">
        <div class="flex space-x-1 items-center">
          <div class="flex items-center">
            <svg
              v-if="message.type === 'msg'"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6 text-green-600"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <svg
              v-if="message.type === 'error'"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6 text-red-500"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            <svg
              v-if="message.type === 'warning'"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6 text-yellow-500"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
            <svg
              v-if="message.type === 'info'"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6 text-blue-400"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
              />
            </svg>
          </div>
          <div class="max-h-[100px] max-w-[300px] overflow-hidden text-sm">
            {{ message.msg }}
          </div>
        </div>
        <button
          class="underline underline-offset-4 decoration-gray-500 hover:decoration-gray-200 transition duration-300 text-xs"
          @click="dismissMessage(message.id)"
        >
          Dismiss
        </button>
      </div>

      <div class="absolute top-0 left-0 right-0 bg-gray-300">
        <div class="relative w-full bg-white/50 rounded-full overflow-hidden">
          <div
            class="h-1"
            :class="{
              'bg-emerald-600': message.type === 'msg',
              'bg-red-600': message.type === 'error',
              'bg-yellow-600': message.type === 'warning',
              'bg-blue-600': message.type === 'info',
            }"
            :style="{
              width: `${(timeUntilDestruction / message.timeout) * 100}%`,
            }"
          />
        </div>
      </div>
    </div>
  </div>
</template>
